import React, { useEffect, useState } from "react";
// MUI Components
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Skeleton,
  Box,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
// MUI Icon
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
// utils
import { gamePerformance } from "../../../Utilities/dashboardSummary";

export const GamePerformance = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const cardsPerPage = isSmallScreen ? 2 : 4;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await gamePerformance();
        if (Array.isArray(response)) {
          setRawData(response);
        } else {
          setRawData([]);
        }
      } catch (error) {
        console.error("Error fetching user engagement:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get the current set of visible cards
  const visibleCards =
    rawData && rawData?.slice(currentIndex, currentIndex + cardsPerPage);

  const handleNext = () => {
    const nextIndex = currentIndex + cardsPerPage;
    if (nextIndex < rawData.length) {
      setCurrentIndex(nextIndex);
    }
  };

  const handlePrev = () => {
    const prevIndex = currentIndex - cardsPerPage;
    setCurrentIndex(Math.max(prevIndex, 0));
  };
  return (
    <React.Fragment>
      <Card
        variant="outlined"
        sx={{
          mt: 2,
          backgroundColor: "#242424",
          borderRadius: "8px",
          padding: "20px",
          position: "relative",
        }}
      >
        {/* Title and Buttons (Aligned to Right) */}
        <Box>
          <Typography
            gutterBottom
            variant="h5"
            component="div"
            mb={0}
            sx={{ fontSize: 22, fontWeight: 500, color: "#FFF" }}
          >
            Game Performance
          </Typography>
        </Box>

        {/* Navigation Buttons (Right Aligned) */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <IconButton
            sx={{
              color: "#FFF",
            }}
            onClick={handlePrev}
            disabled={currentIndex === 0}
          >
            <ArrowCircleLeftIcon sx={{ fontSize: 25 }} />
          </IconButton>

          <IconButton
            sx={{
              color: "#FFF",
            }}
            onClick={handleNext}
            disabled={currentIndex + cardsPerPage >= rawData.length}
          >
            <ArrowCircleRightIcon sx={{ fontSize: 25 }} />
          </IconButton>
        </Box>

        {/* Cards Section */}
        <Grid container spacing={2}>
          {loading ? (
            [...Array(4)].map((_, index) => (
              <Grid item xs={12} sm={6} md={6} key={index}>
                <Card
                  variant="outlined"
                  sx={{
                    mt: 1,
                    border: "1px solid #1A1A1A",
                    background: "#1A1A1A",
                  }}
                >
                  <CardContent>
                    <Skeleton
                      width="60%"
                      height={20}
                      sx={{ mt: 1, background: "#fff" }}
                    />
                    <Skeleton
                      width="40%"
                      height={30}
                      sx={{ mt: 1, background: "#fff" }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : visibleCards?.length === 0 ? (
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: "#FFF",
                  textAlign: "center",
                  mt: 11,
                  mb: 12,
                }}
              >
                No data found
              </Typography>
            </Grid>
          ) : (
            visibleCards?.map((item, index) => (
              <Grid item xs={12} sm={6} md={6} key={index}>
                <Card
                  variant="outlined"
                  sx={{
                    border: "1px solid #1A1A1A",
                    background: "#1A1A1A",
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#FFD700",
                        }}
                      >
                        {item?.gameName}
                      </Typography>
                      <Chip
                        variant="filled"
                        size="small"
                        color={item?.type === "VE" ? "primary" : "secondary"}
                        label={item?.type}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        // mb: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 400,
                          color: "#FFF",
                        }}
                      >
                        RTP :
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 400,
                          color: "rgba(255, 255, 255, 0.70)",
                        }}
                      >
                        {item?.rtp?.toFixed(2)}%
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        // mb: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 400,
                          color: "#FFF",
                        }}
                      >
                        Hit Rate :
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 400,
                          color: "rgba(255, 255, 255, 0.70)",
                        }}
                      >
                        {item?.hitRate?.toFixed(2)}%
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        // mb: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 400,
                          color: "#FFF",
                        }}
                      >
                        Average Bet :
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 400,
                          color: "rgba(255, 255, 255, 0.70)",
                        }}
                      >
                        {item?.averageBet?.toFixed(2)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Card>
    </React.Fragment>
  );
};
